/**
 * LT-18 Phase 3 — Materials Settings tab.
 *
 * Three managed lists on one screen. Categories + suppliers get the
 * confirm-on-new dialog in Phase 4 (from the material form). Units are
 * a closed technical list — the "add" button here is the ONLY entry
 * point, and a typo on the material form is rejected outright.
 */
import React from "react";
import { ManagedListPanel } from "../components/ManagedListPanel";
import { TaxonomyMigrationReport } from "../components/TaxonomyMigrationReport";

const SUPPLIER_FIELDS = [
  { name: "account_ref",   label: "Account ref",   placeholder: "our account number" },
  { name: "contact_name",  label: "Contact name",  placeholder: "" },
  { name: "contact_email", label: "Contact email", placeholder: "" },
  { name: "contact_phone", label: "Contact phone", placeholder: "" },
  { name: "notes",         label: "Notes",         placeholder: "" },
];

// LT-19b §1 — stored and editable here; consumed by nothing until LT-20
// (the quantity engine). Unknown units default to NOT divisible — the
// safe direction, rounding up never leaves anyone short.
const UNIT_TOGGLES = [
  {
    name:  "is_divisible",
    label: "Divisible",
    title: "Ticked: a fractional quantity can be ordered (m, m², kg, litre). "
         + "Unticked: bought in whole units (sheet, bag, pack, each).",
  },
];

export default function MaterialsSettingsTab() {
  return (
    <div className="grid gap-6" style={{ gridTemplateColumns: "1fr 1fr" }}>
      <div className="space-y-6">
        <ManagedListPanel
          title="Categories"
          description="Timber, sheet materials, aggregates, fixings … a material has exactly one category. 'Also known as' = what supplier files call it; the CSV import reads those as this category."
          endpoint="/material-categories"
          labelField="label"
          labelPlaceholder="e.g. Timber"
          inputName="lt-category-label"
          rowAliases
          testId="mats-categories"
        />
        <ManagedListPanel
          title="Units"
          description="Closed technical list. A material's unit MUST come from this list; a typo is rejected. Divisible = a fractional quantity is orderable (55 m² of sand); untick for units bought whole (a sheet, a bag). 'Also known as' (metre, lm …) = what supplier files call it; the CSV import reads those as this unit."
          endpoint="/material-units"
          labelField="label"
          labelPlaceholder="e.g. m²"
          inputName="lt-unit-label"
          rowToggles={UNIT_TOGGLES}
          rowAliases
          superscriptHint
          testId="mats-units"
        />
        <TaxonomyMigrationReport />
      </div>
      <div>
        <ManagedListPanel
          title="Suppliers"
          description="A supplier record. Rename freely — the internal key never changes and materials do not orphan."
          endpoint="/suppliers"
          labelField="name"
          labelPlaceholder="e.g. Travis Perkins"
          // Bug 9 — a non-standard `name` attribute so browsers don't
          // treat this as a login/username field and pop the password
          // vault. Prefix scoped to LuxTender.
          inputName="lt-supplier-display-name"
          createPayload={(values) => ({
            name:          values.label,
            account_ref:   values.account_ref || null,
            contact_name:  values.contact_name || null,
            contact_email: values.contact_email || null,
            contact_phone: values.contact_phone || null,
            notes:         values.notes || null,
          })}
          extraFields={SUPPLIER_FIELDS}
          allowReorder={false}
          testId="mats-suppliers"
        />
      </div>
    </div>
  );
}
